/**
 * アルゴリズム管理プロバイダ（バックエンド完全依存型）
 *
 * 設計原則:
 * - バックエンドが唯一の情報源
 * - フォールバックは使用しない（エラーを隠蔽しないため）
 * - フロントエンドはバックエンドから取得したデータのみ使用
 */

import { API_BASE_URL } from '../services/api';

// パラメータメタデータの型定義
export interface ParameterMetadata {
  key: string;
  display_name_ja: string;
  description: string;
  type: string;  // "number" | "boolean" | "string"
  min?: number;
  max?: number;
  step?: number;
  unit: string;
}

// アルゴリズム情報の型定義（バックエンドのAlgorithmInfoと完全一致）
export interface AlgorithmInfo {
  id: number;
  key: string;
  display_name_ja: string;
  display_name_en: string;
  description_ja: string;
  description: string;  // 後方互換性のため残す
  default_params: Record<string, any>;
  parameters_metadata: ParameterMetadata[];
  characteristics: string[];  // アルゴリズムの特徴（箇条書き）
  // 以下は後方互換性のため残す
  name?: string;  // display_name_jaのエイリアス
  name_en?: string;  // display_name_enのエイリアス
}

/**
 * アルゴリズム管理プロバイダ
 */
export class AlgorithmProvider {
  private static algorithms: AlgorithmInfo[] | null = null;
  private static fetchPromise: Promise<void> | null = null;
  private static fetchError: Error | null = null;

  /**
   * バックエンドからアルゴリズム定義を取得（必須）
   */
  static async fetchAlgorithmsFromBackend(): Promise<void> {
    // 既に取得処理中の場合は待機
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/algorithms`);

        if (!response.ok) {
          throw new Error(`バックエンドAPI呼び出し失敗: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (!Array.isArray(data) || data.length === 0) {
          throw new Error('バックエンドから有効なアルゴリズムデータを取得できませんでした');
        }

        this.algorithms = data;
        this.fetchError = null;
        console.log(`✅ バックエンドから${data.length}個のアルゴリズム定義を取得しました`);
      } catch (error) {
        this.fetchError = error as Error;
        this.algorithms = null;
        console.error('❌ バックエンドからアルゴリズム定義を取得できませんでした:', error);
        throw error;  // エラーを再スローして呼び出し元で処理
      }
    })();

    return this.fetchPromise;
  }

  /**
   * 全アルゴリズム情報を取得
   * @throws バックエンドからデータ取得していない場合はエラー
   */
  static getAllAlgorithms(): AlgorithmInfo[] {
    if (this.fetchError) {
      throw new Error(`アルゴリズムデータ取得エラー: ${this.fetchError.message}`);
    }

    if (!this.algorithms) {
      throw new Error('アルゴリズムデータが未取得です。fetchAlgorithmsFromBackend()を先に呼び出してください。');
    }

    return this.algorithms;
  }

  /**
   * キー名からアルゴリズム情報を取得
   */
  static getAlgorithmByKey(key: string): AlgorithmInfo | undefined {
    return this.getAllAlgorithms().find(algo => algo.key === key);
  }

  /**
   * IDからアルゴリズム情報を取得
   */
  static getAlgorithmById(id: number): AlgorithmInfo | undefined {
    return this.getAllAlgorithms().find(algo => algo.id === id);
  }

  /**
   * 全アルゴリズムのキー名リストを取得
   */
  static getAlgorithmKeys(): string[] {
    return this.getAllAlgorithms().map(algo => algo.key);
  }

  /**
   * アルゴリズムキーと表示名のマッピングを取得
   */
  static getAlgorithmDisplayNames(language: 'ja' | 'en' = 'ja'): Record<string, string> {
    const result: Record<string, string> = {};

    for (const algo of this.getAllAlgorithms()) {
      result[algo.key] = language === 'ja' ? algo.display_name_ja : algo.display_name_en;
    }

    return result;
  }

  /**
   * データ取得状態をチェック
   */
  static isReady(): boolean {
    return this.algorithms !== null && this.fetchError === null;
  }

  /**
   * エラー状態を取得
   */
  static getError(): Error | null {
    return this.fetchError;
  }

  /**
   * キャッシュをクリア（テスト用）
   */
  static clearCache(): void {
    this.algorithms = null;
    this.fetchPromise = null;
    this.fetchError = null;
  }
}

// デフォルトアルゴリズムキーを取得する関数
export const getDefaultAlgorithmKey = (): string => {
  const algorithms = AlgorithmProvider.getAllAlgorithms();
  return algorithms.length > 0 ? algorithms[0].key : '';
};

// デフォルトアルゴリズムキーの定数（後方互換性のため）
// 注意: AlgorithmProvider.getAllAlgorithms()はバックエンドからデータ取得後にのみ使用可能
export const DEFAULT_ALGORITHM_KEY = '';  // 空文字列で初期化、実際にはgetDefaultAlgorithmKey()を使用すること

/**
 * アルゴリズムキーから表示名を取得するヘルパー関数
 */
export const getAlgorithmDisplayName = (algorithmKey: string, language: 'ja' | 'en' = 'ja'): string => {
  const algorithm = AlgorithmProvider.getAlgorithmByKey(algorithmKey);
  if (!algorithm) {
    console.warn(`⚠️ アルゴリズム "${algorithmKey}" が見つかりません`);
    return algorithmKey;  // フォールバックとしてキー名を返す
  }
  return language === 'ja' ? algorithm.display_name_ja : algorithm.display_name_en;
};
